<script lang="ts">
  import Icon from "../common/icons/Icon.svelte";
  import type { SliderItem } from "./type";
  import checkMobileDevice from "../common/is-mobile";
  import EnterWaitlist from "../common/enter-waitlist/EnterWaitlist.svelte";
  import { isWaitlistSubmitted, setWaitlistSubmitted } from "./utils";

  export let item: SliderItem;
  export let isActive: boolean = false;
  export let isPlaceholder: boolean = false;
  export let playInitAnimationEnd: (item: string) => void = () => {};
  export let playIconInitAnimation: boolean = false;
  export let playTitleInitAnimation: boolean = false;
  export let playDescriptionInitAnimation: boolean = false;
  export let playImageInitAnimation: boolean = false;
  export let playWaitlistInitAnimation: boolean = false;
  // export let showSlidePauseNotification: boolean;
  // export let toggleCloseSlidePauseNotification: Function = null;
  /**
   * It is used to control the sliding from inside, for example when user
   * interact with waitlist part, slide should be stopped
   * @param stop
   */
  export let controlSlidingCb = (stop: boolean) => {};

  const isMobile = checkMobileDevice();
  let isWaitlistFocus: boolean = false;

  function submittedSuccessCallback() {
    setWaitlistSubmitted(true);
  }

  $: isSubmitted = $isWaitlistSubmitted;
  $: if (isWaitlistFocus) {
    controlSlidingCb(true);
  } else {
    controlSlidingCb(false);
  }
</script>

<div
  class="slider-item-container"
  class:item-placeholder={isPlaceholder}
  class:mobile={isMobile}
  class:active={isActive}
>
  <div class="item-content">
    <div class="item-icon-container">
      <div
        class="item-icon"
        class:fade-in-icon={playIconInitAnimation}
        on:animationend={() => playInitAnimationEnd("icon")}
      >
        <Icon
          icon={item.icon}
          width={item.iconWidth ?? "30px"}
          height={item.iconHeight ?? "30px"}
        />
      </div>
    </div>

    <div
      class="item-title"
      class:fade-in-title={playTitleInitAnimation}
      on:animationend={() => playInitAnimationEnd("title")}
    >
      {item.title}
    </div>

    <div
      class="item-description"
      class:fade-in-description={playDescriptionInitAnimation}
      on:animationend={() => playInitAnimationEnd("description")}
      style="max-width: {item.descriptionMaxWidth}px"
    >
      {item.descriptions}
    </div>

    <div
      class="join-waitlist"
      class:fade-in-description={playWaitlistInitAnimation}
      on:animationend={() => playInitAnimationEnd("waitlist")}
    >
      <EnterWaitlist
        useIconButton={false}
        inputBoxShadow={true}
        successBoxShadow={true}
        increaseSizeOnSuccess={true}
        {isSubmitted}
        {submittedSuccessCallback}
        bind:isWaitlistFocus
      />
    </div>

    <!-- {#if showSlidePauseNotification}
      <div
        class="pause-notification"
        class:fade-in-image={playImageInitAnimation}
        on:animationend={() => playInitAnimationEnd("image")}
      >
        <div>* Hover image to pause Slide animation</div>

        <div
          class="close-button"
          on:click={() => toggleCloseSlidePauseNotification()}
        >
          <Icon icon="splide-close-notification" size="20px" />
        </div>
      </div>
    {/if} -->

    <div class="slider-image">
      <div
        class="item-img-container"
        class:fade-in-image={playImageInitAnimation}
        on:animationend={() => playInitAnimationEnd("image")}
      >
        <div class="item-img">
          <img src={item.image} alt={item.title} width="1280" height="720" />
        </div>
      </div>
    </div>
  </div>
</div>

<style lang="postcss">
  .slider-item-container {
    position: absolute;
    display: none;
    left: 0px;
    top: 0px;
    bottom: 0px;
    width: 100%;
  }

  .slider-item-container.item-placeholder {
    position: relative;
    display: block;
  }

  .slider-item-container.item-placeholder {
    opacity: 0;
  }

  .slider-item-container.active,
  .slider-item-container:global(.prev),
  .slider-item-container:global(.next) {
    display: block;
  }

  .slider-item-container:global(.prev) {
    left: -100%;
    opacity: 0;
  }

  .slider-item-container.active {
    left: 0px;
  }

  .slider-item-container:global(.next) {
    left: 100%;
    opacity: 0;
  }

  .slider-item-container:global(.next.left) {
    transform: translateX(-100%);
    opacity: 1;
  }

  .slider-item-container.active:global(.left) {
    transform: translateX(-100%);
    opacity: 0;
  }

  .slider-item-container:global(.prev.right) {
    transform: translateX(100%);
    opacity: 1;
  }

  .slider-item-container.active:global(.right) {
    transform: translateX(100%);
    opacity: 0;
  }

  .slider-item-container:global(.next.left),
  .slider-item-container.active:global(.left),
  .slider-item-container:global(.prev.right),
  .slider-item-container.active:global(.right) {
    transition: transform var(--switch-slide-animation-duration) ease-in-out,
      opacity var(--switch-slide-animation-duration) linear;
  }

  .slider-item-container.active:global(.left) .item-img,
  .slider-item-container.active:global(.right) .item-img {
    transform: scale(0.7);
    transform-origin: top;
    transition: transform var(--switch-slide-animation-duration) ease-in-out;
  }

  .slider-item-container:global(.next) .item-img,
  .slider-item-container:global(.prev) .item-img {
    transform: scale(0.8);
    transform-origin: top;
    transition: transform var(--switch-slide-animation-duration) ease-in-out;
  }

  .slider-item-container:global(.next.left) .item-img,
  .slider-item-container:global(.prev.right) .item-img {
    transform: scale(1);
  }

  .slider-item-container:not(.item-placeholder) .item-content {
    @apply flex flex-col w-full h-full items-center justify-center;
  }

  .mobile .slider-image {
    margin: 0px;
  }

  .item-icon-container {
    @apply flex flex-row justify-center mb-5;
  }

  .mobile .item-icon-container {
    @apply mb-4;
  }

  .item-icon {
    @apply flex flex-row items-center justify-center rounded-lg;
    width: 54px;
    height: 54px;
    background: #5f89ff;
    box-shadow: 5px 5px 20px rgba(95, 137, 255, 0.25);
  }

  .item-title {
    @apply mb-2 mx-auto font-bold;
    background: linear-gradient(90.76deg, #5f89ff 0%, #c418ff 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-fill-color: transparent;
    font-size: clamp(2rem, 1.25rem + 1.5vw, 2.5rem);
    max-width: max-content;
  }

  .mobile .item-title {
    font-size: 1.5rem;
  }

  .item-description {
    @apply font-medium text-center;
    margin: 0px auto;
    width: min(max(400px, 90%), 716px);
    padding: 0px 8px;
    color: #a7b0b5;
    font-size: clamp(0.8125rem, 0.75rem + 0.75vw, 1.125rem);
  }

  .slider-item-container:not(.mobile) .item-description {
    line-height: 1.7;
  }

  .mobile .item-description {
    font-size: 13px;
    width: min(100%, 516px);
  }

  /* .pause-notification {
    @apply italic flex flex-row items-center mt-4 rounded font-medium space-x-5 bg-white;
    padding: 6px 12px;
    box-shadow: 2px 4px 10px rgba(55, 84, 170, 0.21);
    color: #454450;
    font-size: 13px;
  }

  .mobile .pause-notification {
    @apply space-x-2;
    font-size: 12px;
  }

  .pause-notification .close-button {
    @apply cursor-pointer;
    color: #a7b0b5;
  } */

  .item-img-container {
    @apply relative rounded-lg;
    background-color: white;
    margin-top: 30px;
    z-index: 1;
    opacity: 1;
  }

  /* .item-img-container.has-pause-notification {
    margin-top: 8px;
  } */

  .item-img-container::before {
    @apply absolute rounded-lg;
    content: "";
    top: 16px;
    bottom: -16px;
    left: 16px;
    right: 16px;
    background: #3754aa;
    opacity: 0.15;
    filter: blur(20px);
    z-index: -1;
  }

  .mobile .item-img-container::before {
    top: 4px;
    bottom: -4px;
    left: 5px;
    right: 5px;
    opacity: 0.15;
    filter: blur(12px);
    border-radius: 8px;
  }

  .item-img {
    /* @apply w-full h-full rounded-md overflow-hidden; */
    @apply w-full h-full;
  }

  img {
    max-width: 100%;
    /* max-width: max(100%, 900px); */
    height: auto;
    border-radius: 6px;
  }

  @media (orientation: portrait) {
    .mobile .item-img img {
      max-width: max(100%, 900px);
    }

    .mobile .item-img-container::before {
      right: unset;
      width: max(calc(100% - 10px), 890px);
    }
  }

  @media (orientation: landscape) {
    .mobile .item-img img {
      max-width: 100%;
    }
  }

  .fade-in-title {
    animation: fadeInDown var(--init-animation-duration) forwards;
    opacity: 0;
  }

  .fade-in-description {
    animation: fadeInDown var(--init-animation-duration) forwards;
    animation-delay: 0.2s;
    opacity: 0;
  }

  .fade-in-icon {
    animation: fadeIn var(--init-animation-duration) ease-in-out;
    animation-delay: 0.3s;
    opacity: 0;
  }

  .fade-in-image {
    animation: fadeIn;
    animation-timing-function: cubic-bezier(0.645, 0.045, 0.355, 1);
    animation-duration: var(--init-animation-duration);
    opacity: 0;
    /* animation-timing-function: ease-in-out; */
  }

  .join-waitlist {
    @apply text-left pt-6 pb-2.5;
    width: min(600px, 100%);
  }
</style>
